import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation, Routes, Route, Navigate } from 'react-router-dom';
import UserProfile from './UserProfile';
import ManagerManagement from './ManagerManagement';
import UsersManagement from './UsersManagement';
import Settings from './Settings';
import Shifts from './Shifts';
import StatisticsManagers from './StatisticsManagers';
import StatisticsJunior from './StatisticsJunior';
import JuniorCalls from './JuniorCalls';
import StatisticsPeriodSummary from './StatisticsPeriodSummary';
import ModeratorAgent from './ModeratorAgent';
import Calendar from './Calendar';
import PrimeTimes from './PrimeTimes';
import Payouts from './Payouts';
import Vacations from './Vacations';
import Applications from './Applications';
import Punishments from './Punishments';
import CleaningConsoleConcept from './CleaningConsoleConcept';
import NotificationsPage from './NotificationsPage';
import DpMark from './DpMark';
import AccessDeniedModal from './AccessDeniedModal';
import BirthDateRequiredModal from './BirthDateRequiredModal';
import Logo from './Logo';
import Overview from './Overview';
import { SITE_ACCESS_ROLES } from '../constants/siteAccessRoles';
import { DEPUTY_AND_ABOVE, CHIEF_AND_ABOVE, ROLES, hasAnyRole } from '../constants/rolePermissions';
import FruitNinja from './FruitNinja';
import './FormModal.css';
import './Dashboard.css';
import { SHIFTS_AND_ONLINE_ENABLED } from '../constants/featureFlags';

/** Включить страницу AI (пока скрыта) */
const MODERATOR_AGENT_ENABLED = false;

const Dashboard = ({ setIsAuthenticated }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('user') || '{}'));
  const handleUserUpdated = useCallback((u) => {
    setUser(u);
    localStorage.setItem('user', JSON.stringify(u));
  }, []);
  const [roleCheckStatus, setRoleCheckStatus] = useState('Проверка...');
  const [userRoles, setUserRoles] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [sessionStartTime, setSessionStartTime] = useState(() => {
    // Получаем время начала сессии из localStorage или создаем новое
    const saved = localStorage.getItem('sessionStartTime');
    if (saved) {
      return new Date(parseInt(saved));
    }
    const now = new Date();
    localStorage.setItem('sessionStartTime', now.getTime().toString());
    return now;
  });
  const [showAccessDenied, setShowAccessDenied] = useState(false);
  const [accessDeniedMessage, setAccessDeniedMessage] = useState('');
  const [menuLayout, setMenuLayout] = useState(() => {
    // На мобильных устройствах всегда используем боковое меню
    if (window.innerWidth <= 768) {
      return 'sidebar';
    }
    const saved = localStorage.getItem('menuLayout');
    return saved || 'sidebar'; // 'sidebar' или 'topbar'
  });
  const [dropdownOpen, setDropdownOpen] = useState({
    statistics: false,
    management: false
  });
  const [mobileBottomMenuOpen, setMobileBottomMenuOpen] = useState({
    main: false,
    moderation: false,
    management: false,
    profile: false
  }); // состояние подменю в нижнем меню
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768); // состояние для проверки мобильного вида
  const [isSettingsOpen, setIsSettingsOpen] = useState(false); // состояние для модального окна настроек
  const [notificationsModalOpen, setNotificationsModalOpen] = useState(false); // модальное окно уведомлений
  const [showSetPinPrompt, setShowSetPinPrompt] = useState(false); // предложение установить PIN после входа без PIN
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);
  const [showFruitNinja, setShowFruitNinja] = useState(false);
  const [statisticsDisplay, setStatisticsDisplay] = useState({
    hide_period_summary: false,
  });

  const fetchStatisticsDisplay = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch('/api/app-settings', { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.statistics_display) {
          setStatisticsDisplay({
            hide_period_summary: false,
            ...data.statistics_display,
          });
        }
      })
      .catch(() => {});
  }, []);

  // При входе проверить, есть ли непросмотренное уведомление (для индикатора у колокольчика и профиля)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch('/api/notifications/letters', { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        if (list.length === 0) return;
        const latestId = list[0].id;
        const lastSeenId = parseInt(localStorage.getItem('notification_last_seen_id') || '0', 10);
        setHasUnreadNotifications(latestId > lastSeenId);
      })
      .catch(() => {});
  }, []);

  // Актуальные данные пользователя (в т.ч. дата рождения) с сервера при открытии панели
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch('/api/auth/verify', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.user) {
          setUser(data.user);
          localStorage.setItem('user', JSON.stringify(data.user));
        }
      })
      .catch(() => {});
  }, []);

  // После входа с нового IP без PIN — предложить установить PIN (только когда дата рождения уже указана)
  useEffect(() => {
    if (!user?.birthDate) return;
    if (sessionStorage.getItem('promptSetPinAfterLogin')) {
      setShowSetPinPrompt(true);
      sessionStorage.removeItem('promptSetPinAfterLogin');
    }
  }, [user?.birthDate]);

  // Определяем активную вкладку из URL
  const getActiveNav = () => {
    const path = location.pathname;
    if (path.includes('/managers') || path.includes('/moderators')) return 'managers';
    if (path.includes('/vacations')) return 'vacations';
    if (path.includes('/users')) return 'users';
    if (path.includes('/shifts')) return 'shifts';
    if (path.includes('/calendar')) return 'calendar';
    if (path.includes('/prime-times')) return 'prime-times';
    if (path.includes('/settings')) return 'settings';
    if (path.includes('/discordmoderation/cleaning')) return 'cleaning';
    if (path.includes('/punishments')) return 'punishments';
    if (path.includes('/statistics-junior')) return 'statistics-junior';
    if (path.includes('/statistics-period')) return 'statistics-period';
    if (path.includes('/moderator-agent')) return 'moderator-agent';
    if (path.includes('/statistics')) return 'statistics';
    if (path.includes('/payouts')) return 'payouts';
    if (path.includes('/applications')) return 'applications';
    return 'overview';
  };
  
  // Проверка доступа к выплатам
  const canAccessPayouts = () => {
    const allowedRoles = ['Deputy Chief Manager', 'Chief Manager', 'admin'];
    return userRoles.some(role => allowedRoles.includes(role));
  };

  // Проверка доступа к отпускам
  const canAccessVacations = () => {
    const allowedRoles = ['Deputy Chief Manager', 'Chief Manager', 'admin'];
    return userRoles && userRoles.some(role => allowedRoles.includes(role));
  };

  // Проверка доступа к настройкам (включая вкладку Трансляция Discord)
  const canAccessSettings = () => {
    const allowedRoles = [ 'Deputy Chief Manager', 'Chief Manager', 'admin'];
    return userRoles && userRoles.some(role => allowedRoles.includes(role));
  };

  // Проверка доступа к управлению пользователями (admin и Chief Manager)
  const canAccessUsers = () => {
    return userRoles.includes('admin') || userRoles.includes('Chief Manager');
  };

  // Проверка доступа к управлению заявками (все авторизованные пользователи)
  const canAccessApplications = () => {
    return userRoles && userRoles.length > 0; // Доступ для всех авторизованных пользователей
  };

  const canShowPeriodSummary = () =>
    statisticsDisplay.hide_period_summary !== true;

  const activeNav = getActiveNav();
  
  // Редирект на /discordmoderation/overview если просто /discordmoderation (и для Curator Media — у них тоже есть Основное)
  useEffect(() => {
    if (location.pathname === '/discordmoderation' || location.pathname === '/discordmoderation/') {
      navigate('/discordmoderation/overview', { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    if (!MODERATOR_AGENT_ENABLED && location.pathname.includes('/moderator-agent')) {
      navigate('/discordmoderation/overview', { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    if (!SHIFTS_AND_ONLINE_ENABLED && location.pathname.includes('/shifts')) {
      navigate('/discordmoderation/overview', { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    fetchStatisticsDisplay();
  }, [fetchStatisticsDisplay, location.pathname]);

  useEffect(() => {
    if (
      statisticsDisplay.hide_period_summary === true &&
      (location.pathname.includes('/statistics-period') ||
        location.pathname.includes('/moderator-agent'))
    ) {
      navigate('/discordmoderation/overview', { replace: true });
    }
  }, [location.pathname, navigate, statisticsDisplay.hide_period_summary]);

  // Обновление времени каждую секунду
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Форматирование времени сессии
  const getSessionTime = () => {
    const now = new Date();
    const diff = now - sessionStartTime;
    const sessionDuration = 2 * 60 * 60 * 1000; // 2 часа в миллисекундах
    const remaining = Math.max(0, sessionDuration - diff);
    
    const totalSeconds = Math.floor(remaining / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    const elapsed = diff;
    const progress = Math.min((elapsed / sessionDuration) * 100, 100);
    
    return {
      remaining,
      hours,
      minutes,
      seconds,
      progress,
      isExpired: remaining === 0
    };
  };

  const formatSessionTime = () => {
    const session = getSessionTime();
    if (session.isExpired) {
      return 'Сессия истекла';
    }
    
    if (session.hours > 0) {
      return `${session.hours}ч ${session.minutes}м`;
    } else if (session.minutes > 0) {
      return `${session.minutes}м ${session.seconds}с`;
    } else {
      return `${session.seconds}с`;
    }
  };

  const handleLogout = (reason = null) => {
    if (reason === 'no_role') {
      // Показываем модальное окно вместо редиректа
      setShowAccessDenied(true);
    } else {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('sessionStartTime');
      setIsAuthenticated(false);
      navigate('/login');
    }
  };

  // Функция проверки ролей
  const checkRoles = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      handleLogout();
      return;
    }

    try {
      const response = await fetch('/api/auth/check-roles', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok || !data.hasRequiredRole) {
        if (data.error === 'Пользователь не найден на сервере' || !data.isMember) {
          setRoleCheckStatus('❌ Вы не являетесь участником сервера');
          setAccessDeniedMessage('Вы не являетесь участником Discord сервера. Вы потеряли доступ к системе.');
          setTimeout(() => handleLogout('no_role'), 2000);
        } else {
          setRoleCheckStatus('❌ Нет необходимой роли для доступа');
          setAccessDeniedMessage('Ваша роль была удалена. Вы потеряли доступ к системе.');
          setTimeout(() => handleLogout('no_role'), 2000);
        }
        return;
      }

      const rolesText = data.roles && data.roles.length > 0 
        ? data.roles.filter(role => SITE_ACCESS_ROLES.includes(role)).join(', ')
        : 'Роли проверены';
      setRoleCheckStatus(`✅ ${rolesText || 'Доступ разрешён'}`);
      setUserRoles(data.roles || []);
    } catch (error) {
      console.error('Ошибка при проверке ролей:', error);
      setRoleCheckStatus('⚠️ Ошибка при проверке ролей');
    }
  };

  // Периодическая проверка ролей каждые 20 секунд
  useEffect(() => {
    checkRoles();
    const interval = setInterval(() => {
      checkRoles();
    }, 20000);
    return () => clearInterval(interval);
  }, []);

  // Форматирование даты
  const formatDate = (date) => {
    const days = ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота'];
    const months = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
    return `${days[date.getDay()]}, ${date.getDate()} ${months[date.getMonth()]}`;
  };

  // Форматирование времени
  const formatTime = (date) => {
    return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  };

  // Приветствие в зависимости от времени
  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour >= 5 && hour < 12) return 'Доброе утро';
    if (hour >= 12 && hour < 17) return 'Добрый день';
    if (hour >= 17 && hour < 22) return 'Добрый вечер';
    return 'Доброй ночи';
  };

  const username = user.discordUsername || user.email?.split('@')[0] || 'Пользователь';

  // Закрытие подменю при навигации на мобильных
  const handleNavClick = (path) => {
    navigate(path);
    setMobileBottomMenuOpen({ main: false, moderation: false, management: false });
  };

  // Функция для закрытия всех подменю
  const closeAllSubmenus = () => {
    setMobileBottomMenuOpen({ main: false, moderation: false, management: false, profile: false });
  };

  // Функция для переключения подменю
  const toggleSubmenu = (menuType) => {
    setMobileBottomMenuOpen(prev => {
      const newState = { main: false, moderation: false, management: false, profile: false };
      newState[menuType] = !prev[menuType];
      return newState;
    });
  };

  // Функция для закрытия всех подменю
  const closeAllBottomSubmenus = () => {
    setMobileBottomMenuOpen({ main: false, moderation: false, management: false, profile: false });
  };

  // Переключение расположения меню
  const toggleMenuLayout = () => {
    // На мобильных устройствах не разрешаем переключение
    if (window.innerWidth <= 768) {
      return;
    }
    const newLayout = menuLayout === 'sidebar' ? 'topbar' : 'sidebar';
    setMenuLayout(newLayout);
    localStorage.setItem('menuLayout', newLayout);
  };

  // Обработка изменения размера окна
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth <= 768;
      setIsMobileView(isMobile);
      if (isMobile && menuLayout === 'topbar') {
        setMenuLayout('sidebar');
      }
    };
    handleResize(); // Проверяем при монтировании
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [menuLayout]);

  // Переключение выпадающего меню
  const toggleDropdown = (menu) => {
    setDropdownOpen(prev => ({
      ...prev,
      [menu]: !prev[menu]
    }));
  };

  // Закрытие выпадающего меню при клике вне его
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.topbar-dropdown')) {
        setDropdownOpen({
          statistics: false,
          management: false
        });
      }
    };

    if (dropdownOpen.statistics || dropdownOpen.management) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [dropdownOpen]);

  // Функция для рендеринга элементов навигации
  const renderNavItems = () => {
    const navItems = [
      { id: 'overview', path: '/discordmoderation/overview', icon: 'overview', label: 'Основное', section: 'main' },
      { id: 'calendar', path: '/discordmoderation/calendar', icon: 'calendar', label: 'Календарь', section: 'main' },
      { id: 'prime-times', path: '/discordmoderation/prime-times', icon: 'clock', label: 'Прайм-таймы', section: 'main' },
      ...(SHIFTS_AND_ONLINE_ENABLED
        ? [{ id: 'shifts', path: '/discordmoderation/shifts', icon: 'shifts', label: 'Смены', section: 'main' }]
        : []),
      ...(canShowPeriodSummary()
        ? [
            { id: 'statistics-period', path: '/discordmoderation/statistics-period', icon: 'periodTotals', label: 'Итоговая сводка', section: 'main' },
            { id: 'moderator-agent', path: '/discordmoderation/moderator-agent', icon: 'ai', label: 'AI', section: 'main', locked: !MODERATOR_AGENT_ENABLED },
          ]
        : []),
      { id: 'punishments', path: '/discordmoderation/punishments', icon: 'punishments', label: 'Наказания', section: 'moderation' },
      { id: 'statistics', path: '/discordmoderation/statistics', icon: 'stats', label: 'Статистика - Managers', section: 'moderation' },
      { id: 'statistics-junior', path: '/discordmoderation/statistics-junior', icon: 'stats', label: 'Статистика - Junior', section: 'moderation' },
      { id: 'managers', path: '/discordmoderation/managers', icon: 'managers', label: 'Управление менеджерами', section: 'management' },
    ];

    if (canAccessPayouts()) {
      navItems.push({ id: 'payouts', path: '/discordmoderation/payouts', icon: 'payouts', label: 'Выплаты', section: 'management' });
    }
    if (canAccessVacations()) {
      navItems.push({ id: 'vacations', path: '/discordmoderation/vacations', icon: 'vacations', label: 'Отпуска', section: 'management' });
    }
    if (canAccessSettings()) {
      navItems.push({ id: 'settings', path: '/discordmoderation/settings', icon: 'settings', label: 'Настройки', section: 'management' });
    }
    if (canAccessUsers()) {
      navItems.push({ id: 'users', path: '/discordmoderation/users', icon: 'users', label: 'Пользователи', section: 'management' });
    }
    if (canAccessApplications()) {
      navItems.push({ id: 'applications', path: '/discordmoderation/applications', icon: 'applications', label: 'Заявки', section: 'management' });
    }

    return navItems;
  };

  // Получение SVG иконки
  const getNavIcon = (iconType) => {
    const icons = {
      overview: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="7" height="7"></rect>
          <rect x="14" y="3" width="7" height="7"></rect>
          <rect x="14" y="14" width="7" height="7"></rect>
          <rect x="3" y="14" width="7" height="7"></rect>
        </svg>
      ),
      calendar: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
          <line x1="16" y1="2" x2="16" y2="6"></line>
          <line x1="8" y1="2" x2="8" y2="6"></line>
          <line x1="3" y1="10" x2="21" y2="10"></line>
          <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01"></path>
        </svg>
      ),
      clock: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <polyline points="12 6 12 12 16 14"></polyline>
        </svg>
      ),
      shifts: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
          <circle cx="9" cy="7" r="4"></circle>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
        </svg>
      ),
      stats: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="18" y1="20" x2="18" y2="10"></line>
          <line x1="12" y1="20" x2="12" y2="4"></line>
          <line x1="6" y1="20" x2="6" y2="14"></line>
        </svg>
      ),
      ai: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 3v2" />
          <path d="M12 19v2" />
          <path d="M3 12h2" />
          <path d="M19 12h2" />
          <path d="M5.6 5.6l1.4 1.4" />
          <path d="M17 17l1.4 1.4" />
          <path d="M17 7l1.4-1.4" />
          <path d="M5.6 18.4l1.4-1.4" />
          <circle cx="12" cy="12" r="4" />
        </svg>
      ),
      summary: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
          <line x1="16" y1="2" x2="16" y2="6"></line>
          <line x1="8" y1="2" x2="8" y2="6"></line>
          <line x1="3" y1="10" x2="21" y2="10"></line>
        </svg>
      ),
      periodTotals: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
          <rect x="9" y="3" width="6" height="4" rx="1" />
          <line x1="8" y1="13" x2="16" y2="13" />
          <line x1="8" y1="17" x2="12" y2="17" />
          <line x1="15" y1="11" x2="15" y2="17" />
          <line x1="18" y1="9" x2="18" y2="17" />
          <line x1="12" y1="13" x2="12" y2="17" />
        </svg>
      ),
      phone: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
        </svg>
      ),
      managers: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
          <circle cx="9" cy="7" r="4"></circle>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
        </svg>
      ),
      payouts: <DpMark />,
      settings: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"></path>
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
        </svg>
      ),
      management: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
          <path d="M2 17l10 5 10-5"></path>
          <path d="M2 12l10 5 10-5"></path>
        </svg>
      ),
      users: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
          <circle cx="9" cy="7" r="4"></circle>
          <line x1="19" y1="8" x2="19" y2="14"></line>
          <line x1="22" y1="11" x2="16" y2="11"></line>
        </svg>
      ),
      vacations: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
          <line x1="16" y1="2" x2="16" y2="6"></line>
          <line x1="8" y1="2" x2="8" y2="6"></line>
          <line x1="3" y1="10" x2="21" y2="10"></line>
          <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01"></path>
        </svg>
      ),
      applications: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
          <polyline points="10 9 9 9 8 9"></polyline>
        </svg>
      ),
      punishments: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
        </svg>
      ),
      cleaning: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <path d="M12 8v4M12 16h.01" />
        </svg>
      ),
    };
    return icons[iconType] || icons.overview;
  };

  const needsBirthDate = Boolean(user?.id) && !user.birthDate;

  return (
    <div className="dashboard-layout">
      {needsBirthDate && (
        <BirthDateRequiredModal onComplete={handleUserUpdated} />
      )}
      {showFruitNinja && <FruitNinja onClose={() => setShowFruitNinja(false)} />}
      {/* Мобильная верхняя панель с логотипом */}
      {isMobileView && menuLayout === 'sidebar' && (
        <div className="mobile-top-header">
          <div className="mobile-logo-container">
            <Logo variant="mobile" onDoubleClick={() => setShowFruitNinja(true)} />
          </div>
        </div>
      )}

      {/* Боковая панель */}
      {menuLayout === 'sidebar' && (
        <aside className="dashboard-sidebar">
        {/* Логотип в боковом меню только на десктопе */}
        <div className="sidebar-header">
          <Logo variant="sidebar" style={{ cursor: 'pointer' }} className="sidebar-logo" onDoubleClick={() => setShowFruitNinja(true)} />
        </div>

        <nav className="sidebar-nav">
          <div className="nav-section">
            <div className="nav-section-title">ГЛАВНОЕ</div>
            <button 
              className={`nav-item ${activeNav === 'overview' ? 'active' : ''}`}
              onClick={() => handleNavClick('/discordmoderation/overview')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7"></rect>
                <rect x="14" y="3" width="7" height="7"></rect>
                <rect x="14" y="14" width="7" height="7"></rect>
                <rect x="3" y="14" width="7" height="7"></rect>
              </svg>
              <span>Основное</span>
            </button>
            <button 
              className={`nav-item ${activeNav === 'calendar' ? 'active' : ''}`}
              onClick={() => handleNavClick('/discordmoderation/calendar')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
                <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01"></path>
              </svg>
              <span>Календарь</span>
            </button>
            <button 
              className={`nav-item ${activeNav === 'prime-times' ? 'active' : ''}`}
              onClick={() => handleNavClick('/discordmoderation/prime-times')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              <span>Прайм-таймы</span>
            </button>
            {SHIFTS_AND_ONLINE_ENABLED && (
            <button 
              className={`nav-item ${activeNav === 'shifts' ? 'active' : ''}`}
              onClick={() => handleNavClick('/discordmoderation/shifts')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
              <span>Смены</span>
            </button>
            )}
            {canShowPeriodSummary() && (
              <>
                <button
                  className={`nav-item ${activeNav === 'statistics-period' ? 'active' : ''}`}
                  onClick={() => handleNavClick('/discordmoderation/statistics-period')}
                >
                  {getNavIcon('periodTotals')}
                  <span>Итоговая сводка</span>
                </button>
                {MODERATOR_AGENT_ENABLED ? (
                  <button
                    className={`nav-item ${activeNav === 'moderator-agent' ? 'active' : ''}`}
                    onClick={() => handleNavClick('/discordmoderation/moderator-agent')}
                  >
                    {getNavIcon('ai')}
                    <span>AI</span>
                  </button>
                ) : (
                  <button type="button" className="nav-item nav-item--soon" disabled aria-disabled="true">
                    {getNavIcon('ai')}
                    <span>AI</span>
                  </button>
                )}
              </>
            )}
          </div>

          <div className="nav-section">
            <div className="nav-section-title">МОДЕРАЦИЯ</div>
              <button 
                className={`nav-item ${activeNav === 'punishments' ? 'active' : ''}`}
                onClick={() => handleNavClick('/discordmoderation/punishments')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                </svg>
                <span>Наказания</span>
              </button>
              <button 
                className={`nav-item ${activeNav === 'statistics' ? 'active' : ''}`}
                onClick={() => handleNavClick('/discordmoderation/statistics')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="20" x2="18" y2="10"></line>
                  <line x1="12" y1="20" x2="12" y2="4"></line>
                  <line x1="6" y1="20" x2="6" y2="14"></line>
                </svg>
                <span>Статистика - Managers</span>
              </button>
              <button 
                className={`nav-item ${activeNav === 'statistics-junior' ? 'active' : ''}`}
                onClick={() => handleNavClick('/discordmoderation/statistics-junior')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="20" x2="18" y2="10"></line>
                  <line x1="12" y1="20" x2="12" y2="4"></line>
                  <line x1="6" y1="20" x2="6" y2="14"></line>
                </svg>
                <span>Статистика - Junior</span>
              </button>
            </div>

          <div className="nav-section">
              <div className="nav-section-title">УПРАВЛЕНИЕ</div>
              <button 
                className={`nav-item ${activeNav === 'managers' ? 'active' : ''}`}
                onClick={() => handleNavClick('/discordmoderation/managers')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
                <span>Управление менеджерами</span>
              </button>
              
              {canAccessVacations() && (
                <button 
                  className={`nav-item ${activeNav === 'vacations' ? 'active' : ''}`}
                  onClick={() => handleNavClick('/discordmoderation/vacations')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                    <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01"></path>
                  </svg>
                  <span>Отпуска</span>
                </button>
              )}
              
              {canAccessPayouts() && (
                <button 
                  className={`nav-item ${activeNav === 'payouts' ? 'active' : ''}`}
                  onClick={() => handleNavClick('/discordmoderation/payouts')}
                >
                {getNavIcon('payouts')}
                <span>Выплаты</span>
              </button>
            )}
            {canAccessSettings() && (
              <button 
                className={`nav-item ${activeNav === 'settings' ? 'active' : ''}`}
                onClick={() => handleNavClick('/discordmoderation/settings')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"></path>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                </svg>
                <span>Настройки</span>
              </button>
            )}
            {canAccessUsers() && (
              <button 
                className={`nav-item ${activeNav === 'users' ? 'active' : ''}`}
                onClick={() => handleNavClick('/discordmoderation/users')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <line x1="19" y1="8" x2="19" y2="14"></line>
                  <line x1="22" y1="11" x2="16" y2="11"></line>
                </svg>
                <span>Пользователи</span>
              </button>
            )}
            {canAccessApplications() && (
              <button 
                className={`nav-item ${activeNav === 'applications' ? 'active' : ''}`}
                onClick={() => handleNavClick('/discordmoderation/applications')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
                <span>Заявки</span>
              </button>
            )}
          </div>
        </nav>

        <div className="sidebar-footer">
          {window.innerWidth > 768 && (
            <div className="menu-layout-toggle">
              <button 
                className="layout-toggle-btn"
                onClick={toggleMenuLayout}
                title={menuLayout === 'sidebar' ? 'Переключить на верхнее меню' : 'Переключить на боковое меню'}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  {menuLayout === 'sidebar' ? (
                    <path d="M3 12h18M3 6h18M3 18h18"></path>
                  ) : (
                    <path d="M4 6h16M4 12h16M4 18h16"></path>
                  )}
                </svg>
                <span>{menuLayout === 'sidebar' ? 'Верхнее меню' : 'Боковое меню'}</span>
              </button>
            </div>
          )}
          <div className="sidebar-profile-row">
            <UserProfile 
              user={user}
              onLogout={() => handleLogout()}
              roleCheckStatus={roleCheckStatus}
              isOpen={isSettingsOpen}
              onClose={() => setIsSettingsOpen(false)}
              isMobileView={isMobileView}
              onUserUpdated={handleUserUpdated}
            />
            <button
              type="button"
              className={`sidebar-notification-bell sidebar-notification-bell--in-profile ${notificationsModalOpen ? 'active' : ''} ${hasUnreadNotifications ? 'has-unread' : ''}`}
              title="Уведомления"
              onClick={(e) => { e.stopPropagation(); setHasUnreadNotifications(false); setNotificationsModalOpen(true); }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
              </svg>
              {hasUnreadNotifications && <span className="notification-bell-dot" aria-hidden="true" />}
            </button>
          </div>
        </div>
      </aside>
      )}

      {/* Верхнее меню */}
      {menuLayout === 'topbar' && (
        <nav className="topbar-nav">
          <div className="topbar-container">
            <div className="topbar-logo">
              <Logo variant="topbar" style={{ cursor: 'pointer' }} onDoubleClick={() => setShowFruitNinja(true)} />
            </div>
            <div className="topbar-menu">
              <>
                  <div className="topbar-section">
                    {renderNavItems().filter(item => item.section === 'main').map(item => (
                      <button
                        key={item.id}
                        type="button"
                        className={`topbar-item ${item.locked ? 'topbar-item--soon' : ''} ${!item.locked && activeNav === item.id ? 'active' : ''}`}
                        disabled={item.locked}
                        aria-disabled={item.locked || undefined}
                        onClick={() => !item.locked && handleNavClick(item.path)}
                      >
                        {getNavIcon(item.icon)}
                        <span>{item.label}</span>
                      </button>
                    ))}
                  </div>
                  <div className="topbar-divider"></div>
                  <div className="topbar-section">
                    <div className="topbar-dropdown">
                      <button
                        className={`topbar-item topbar-dropdown-toggle ${renderNavItems().filter(item => item.section === 'moderation').some(item => activeNav === item.id) ? 'active' : ''} ${dropdownOpen.statistics ? 'dropdown-open' : ''}`}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDropdownOpen(prev => ({ ...prev, statistics: !prev.statistics, management: false }));
                        }}
                      >
                        {getNavIcon('stats')}
                        <span>Статистика</span>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="dropdown-arrow">
                          <path d="M6 9l6 6 6-6"></path>
                        </svg>
                      </button>
                      <div 
                        className="topbar-dropdown-menu"
                        style={{
                          display: dropdownOpen.statistics ? 'block' : 'none'
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {renderNavItems().filter(item => item.section === 'moderation').map(item => (
                          <button
                            key={item.id}
                            type="button"
                            className={`topbar-dropdown-item ${activeNav === item.id ? 'active' : ''}`}
                            onClick={() => {
                              handleNavClick(item.path);
                              setDropdownOpen(prev => ({ ...prev, statistics: false }));
                            }}
                          >
                            {getNavIcon(item.icon)}
                            <span>{item.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="topbar-divider"></div>
                  <div className="topbar-section">
                    <div className="topbar-dropdown">
                      <button
                        className={`topbar-item topbar-dropdown-toggle ${renderNavItems().filter(item => item.section === 'management').some(item => activeNav === item.id) ? 'active' : ''} ${dropdownOpen.management ? 'dropdown-open' : ''}`}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDropdownOpen(prev => ({ ...prev, management: !prev.management, statistics: false }));
                        }}
                      >
                        {getNavIcon('management')}
                        <span>Управление</span>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="dropdown-arrow">
                          <path d="M6 9l6 6 6-6"></path>
                        </svg>
                      </button>
                      <div 
                        className="topbar-dropdown-menu"
                        style={{
                          display: dropdownOpen.management ? 'block' : 'none'
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {renderNavItems().filter(item => item.section === 'management').map(item => (
                          <button
                            key={item.id}
                            type="button"
                            className={`topbar-dropdown-item ${activeNav === item.id ? 'active' : ''}`}
                            onClick={() => {
                              handleNavClick(item.path);
                              setDropdownOpen(prev => ({ ...prev, management: false }));
                            }}
                          >
                            {getNavIcon(item.icon)}
                            <span>{item.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
            </div>
            <div className="topbar-actions">
              {window.innerWidth > 768 && (
                <button 
                  className="topbar-layout-toggle"
                  onClick={toggleMenuLayout}
                  title="Переключить на боковое меню"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 6h16M4 12h16M4 18h16"></path>
                  </svg>
                </button>
              )}
              <div className="topbar-profile-group">
                <button
                  type="button"
                  className={`topbar-notification-bell ${notificationsModalOpen ? 'active' : ''} ${hasUnreadNotifications ? 'has-unread' : ''}`}
                  title="Уведомления"
                  onClick={() => { setHasUnreadNotifications(false); setNotificationsModalOpen(true); }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                  </svg>
                  {hasUnreadNotifications && <span className="notification-bell-dot" aria-hidden="true" />}
                </button>
                <UserProfile 
                user={user}
                onLogout={() => handleLogout()}
                roleCheckStatus={roleCheckStatus}
                isOpen={isSettingsOpen}
                onClose={() => setIsSettingsOpen(false)}
                isMobileView={isMobileView}
                onUserUpdated={handleUserUpdated}
              />
              </div>
            </div>
          </div>
        </nav>
      )}


      {/* Основная область */}
      <main className={`dashboard-main ${menuLayout === 'topbar' ? 'dashboard-main-topbar' : ''} ${location.pathname.includes('prime-times') ? 'dashboard-main-prime-times' : ''} ${location.pathname.includes('vacations') ? 'dashboard-main-vacations' : ''}`}>

      {/* Контент */}
      <div className="main-content" key={location.pathname}>
          <Routes location={location} key={location.pathname}>
            <Route
              path="/overview"
              element={
                <Overview
                  username={username}
                  getGreeting={getGreeting}
                  currentTime={currentTime}
                  user={user}
                  userRoles={userRoles}
                />
              }
            />
            {SHIFTS_AND_ONLINE_ENABLED && (
              <Route path="/shifts" element={<ShiftsContent />} />
            )}
            <Route path="/calendar" element={<Calendar />} />
            <Route path="/prime-times" element={<PrimeTimes />} />
            <Route path="/managers" element={<ManagerManagement />} />
            <Route path="/moderators" element={<Navigate to="/discordmoderation/managers" replace />} />
            <Route path="/users" element={<UsersManagement />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/punishments" element={<Punishments />} />
            <Route
              path="/message-relay"
              element={<Navigate to="/discordmoderation/settings?tab=message-relay" replace />}
            />
            <Route path="/cleaning" element={<CleaningConsoleConcept />} />
            <Route path="/cleaning-concept" element={<Navigate to="/discordmoderation/cleaning" replace />} />
            <Route path="/statistics" element={<StatisticsManagers />} />
            <Route
              path="/statistics-period"
              element={
                canShowPeriodSummary() ? (
                  <StatisticsPeriodSummary />
                ) : (
                  <Navigate to="/discordmoderation/overview" replace />
                )
              }
            />
            {MODERATOR_AGENT_ENABLED && (
              <Route
                path="/moderator-agent"
                element={
                  canShowPeriodSummary() ? (
                    <ModeratorAgent />
                  ) : (
                    <Navigate to="/discordmoderation/overview" replace />
                  )
                }
              />
            )}
            <Route path="/statistics-junior" element={<StatisticsJunior />} />
            <Route path="/junior-calls" element={<JuniorCalls />} />
            <Route path="/payouts" element={<Payouts />} />
            <Route path="/vacations" element={<Vacations />} />
            <Route path="/applications" element={<Applications />} />
            <Route path="*" element={<Navigate to="/discordmoderation/overview" replace />} />
          </Routes>
        </div>
      </main>

      {/* Выпадающая панель уведомлений */}
      {notificationsModalOpen && (
        <>
          <div className="notif-popover-backdrop" onClick={() => setNotificationsModalOpen(false)} />
          <div className={`notif-popover-panel ${menuLayout === 'sidebar' ? 'notif-popover--sidebar' : 'notif-popover--topbar'}`} onClick={(e) => e.stopPropagation()}>
            <NotificationsPage asModal onClose={() => setNotificationsModalOpen(false)} />
          </div>
        </>
      )}

      {/* Нижнее меню для мобильной версии */}
      {isMobileView && menuLayout === 'sidebar' && (
        <>
          {/* Overlay для подменю — не показывать, когда открыты настройки профиля, чтобы клик не закрывал модалку */}
          <div 
            className={`mobile-bottom-menu-overlay ${(mobileBottomMenuOpen.main || mobileBottomMenuOpen.moderation || mobileBottomMenuOpen.management || mobileBottomMenuOpen.profile) && !isSettingsOpen ? 'open' : ''}`}
            onClick={() => {
              setMobileBottomMenuOpen({ main: false, moderation: false, management: false, profile: false });
              setIsSettingsOpen(false);
            }}
          />
          
          <>
              {/* Подменю "Главное" — для всех, в т.ч. Curator Media */}
              <div className={`mobile-bottom-submenu ${mobileBottomMenuOpen.main ? 'open' : ''}`}>
                <div className="mobile-bottom-submenu-header">
                  <span>Главное</span>
                  <button 
                    className="mobile-bottom-submenu-close"
                    onClick={closeAllSubmenus}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="mobile-bottom-submenu-content">
                  <button 
                    className={`mobile-bottom-submenu-item ${activeNav === 'overview' ? 'active' : ''}`}
                    onClick={() => {
                      handleNavClick('/discordmoderation/overview');
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="7" height="7"></rect>
                      <rect x="14" y="3" width="7" height="7"></rect>
                      <rect x="14" y="14" width="7" height="7"></rect>
                      <rect x="3" y="14" width="7" height="7"></rect>
                    </svg>
                    <span>Основное</span>
                  </button>
                  <button 
                    className={`mobile-bottom-submenu-item ${activeNav === 'calendar' ? 'active' : ''}`}
                    onClick={() => {
                      handleNavClick('/discordmoderation/calendar');
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                    <span>Календарь</span>
                  </button>
                  <button 
                    className={`mobile-bottom-submenu-item ${activeNav === 'prime-times' ? 'active' : ''}`}
                    onClick={() => handleNavClick('/discordmoderation/prime-times')}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                    <span>Прайм-таймы</span>
                  </button>
                  {SHIFTS_AND_ONLINE_ENABLED && (
                  <button 
                    className={`mobile-bottom-submenu-item ${activeNav === 'shifts' ? 'active' : ''}`}
                    onClick={() => {
                      handleNavClick('/discordmoderation/shifts');
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                    </svg>
                    <span>Смены</span>
                  </button>
                  )}
                  {canShowPeriodSummary() && (
                    <>
                      <button
                        className={`mobile-bottom-submenu-item ${activeNav === 'statistics-period' ? 'active' : ''}`}
                        onClick={() => {
                          handleNavClick('/discordmoderation/statistics-period');
                        }}
                      >
                        {getNavIcon('periodTotals')}
                        <span>Итоговая сводка</span>
                      </button>
                      {MODERATOR_AGENT_ENABLED ? (
                        <button
                          className={`mobile-bottom-submenu-item ${activeNav === 'moderator-agent' ? 'active' : ''}`}
                          onClick={() => {
                            handleNavClick('/discordmoderation/moderator-agent');
                          }}
                        >
                          {getNavIcon('ai')}
                          <span>AI</span>
                        </button>
                      ) : (
                        <button type="button" className="mobile-bottom-submenu-item mobile-bottom-submenu-item--soon" disabled aria-disabled="true">
                          {getNavIcon('ai')}
                          <span>AI</span>
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Подменю "Модерация" — для всех; у Curator Media только Статистика Media */}
              <div className={`mobile-bottom-submenu ${mobileBottomMenuOpen.moderation ? 'open' : ''}`}>
                <div className="mobile-bottom-submenu-header">
                  <span>Модерация</span>
                  <button 
                    className="mobile-bottom-submenu-close"
                    onClick={closeAllSubmenus}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="mobile-bottom-submenu-content">
                  <button 
                    className={`mobile-bottom-submenu-item ${activeNav === 'punishments' ? 'active' : ''}`}
                    onClick={() => handleNavClick('/discordmoderation/punishments')}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                    </svg>
                    <span>Наказания</span>
                  </button>
                  <button 
                    className={`mobile-bottom-submenu-item ${activeNav === 'statistics' ? 'active' : ''}`}
                    onClick={() => handleNavClick('/discordmoderation/statistics')}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="20" x2="18" y2="10"></line>
                      <line x1="12" y1="20" x2="12" y2="4"></line>
                      <line x1="6" y1="20" x2="6" y2="14"></line>
                    </svg>
                    <span>Статистика - Managers</span>
                  </button>
                  <button 
                    className={`mobile-bottom-submenu-item ${activeNav === 'statistics-junior' ? 'active' : ''}`}
                    onClick={() => handleNavClick('/discordmoderation/statistics-junior')}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="20" x2="18" y2="10"></line>
                      <line x1="12" y1="20" x2="12" y2="4"></line>
                      <line x1="6" y1="20" x2="6" y2="14"></line>
                    </svg>
                    <span>Статистика - Junior</span>
                  </button>
                </div>
              </div>

              <>
              {/* Подменю "Управление" */}
              <div className={`mobile-bottom-submenu ${mobileBottomMenuOpen.management ? 'open' : ''}`}>
                <div className="mobile-bottom-submenu-header">
                  <span>Управление</span>
                  <button 
                    className="mobile-bottom-submenu-close"
                    onClick={closeAllSubmenus}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="mobile-bottom-submenu-content">
                  <button 
                    className={`mobile-bottom-submenu-item ${activeNav === 'managers' ? 'active' : ''}`}
                    onClick={() => handleNavClick('/discordmoderation/managers')}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                    </svg>
                    <span>Управление менеджерами</span>
                  </button>
              {canAccessPayouts() && (
                <button 
                  className={`mobile-bottom-submenu-item ${activeNav === 'payouts' ? 'active' : ''}`}
                  onClick={() => handleNavClick('/discordmoderation/payouts')}
                >
                  {getNavIcon('payouts')}
                  <span>Выплаты</span>
                </button>
              )}
              {canAccessVacations() && (
                <button 
                  className={`mobile-bottom-submenu-item ${activeNav === 'vacations' ? 'active' : ''}`}
                  onClick={() => handleNavClick('/discordmoderation/vacations')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                  <span>Отпуска</span>
                </button>
              )}
              {canAccessSettings() && (
                <button 
                  className={`mobile-bottom-submenu-item ${activeNav === 'settings' ? 'active' : ''}`}
                  onClick={() => handleNavClick('/discordmoderation/settings')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"></path>
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                  </svg>
                  <span>Настройки</span>
                </button>
              )}
              {canAccessUsers() && (
                <button 
                  className={`mobile-bottom-submenu-item ${activeNav === 'users' ? 'active' : ''}`}
                  onClick={() => handleNavClick('/discordmoderation/users')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <line x1="19" y1="8" x2="19" y2="14"></line>
                    <line x1="22" y1="11" x2="16" y2="11"></line>
                  </svg>
                  <span>Пользователи</span>
                </button>
              )}
              {canAccessApplications() && (
                <button 
                  className={`mobile-bottom-submenu-item ${activeNav === 'applications' ? 'active' : ''}`}
                  onClick={() => handleNavClick('/discordmoderation/applications')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                    <polyline points="10 9 9 9 8 9"></polyline>
                  </svg>
                  <span>Заявки</span>
                </button>
              )}
            </div>
          </div>
              </>

              {/* Подменю "Профиль" — в том же формате, что Управление */}
              <div className={`mobile-bottom-submenu ${mobileBottomMenuOpen.profile ? 'open' : ''}`}>
                <div className="mobile-bottom-submenu-header">
                  <span>Профиль</span>
                  <button 
                    className="mobile-bottom-submenu-close"
                    onClick={closeAllSubmenus}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="mobile-bottom-submenu-content">
                  <button 
                    className={`mobile-bottom-submenu-item ${isSettingsOpen ? 'active' : ''}`}
                    onClick={() => {
                      closeAllBottomSubmenus();
                      setIsSettingsOpen(true);
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                      <circle cx="12" cy="12" r="3"></circle>
                      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                    </svg>
                    <span>Настройки профиля</span>
                  </button>
                  <button 
                    className={`mobile-bottom-submenu-item ${notificationsModalOpen ? 'active' : ''}`}
                    onClick={() => {
                      closeAllBottomSubmenus();
                      setHasUnreadNotifications(false);
                      setNotificationsModalOpen(true);
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                      <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                    </svg>
                    <span>Уведомления</span>
                  </button>
                </div>
              </div>

          </>
          {/* Основное нижнее меню */}
          <nav className="mobile-bottom-nav">
            <>
                <button 
                  className={`mobile-bottom-nav-item ${mobileBottomMenuOpen.main || ['overview', 'calendar', 'prime-times', ...(SHIFTS_AND_ONLINE_ENABLED ? ['shifts'] : []), 'statistics-period', 'moderator-agent'].includes(activeNav) ? 'active' : ''}`}
                  onClick={() => toggleSubmenu('main')}
                >
                  <div className="mobile-bottom-nav-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="7" height="7"></rect>
                      <rect x="14" y="3" width="7" height="7"></rect>
                      <rect x="14" y="14" width="7" height="7"></rect>
                      <rect x="3" y="14" width="7" height="7"></rect>
                    </svg>
                  </div>
                  <span className="mobile-bottom-nav-label">Главное</span>
                </button>
                <button 
                  className={`mobile-bottom-nav-item ${mobileBottomMenuOpen.moderation || ['statistics', 'statistics-junior', 'punishments'].includes(activeNav) ? 'active' : ''}`}
                  onClick={() => toggleSubmenu('moderation')}
                >
                  <div className="mobile-bottom-nav-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="20" x2="18" y2="10"></line>
                      <line x1="12" y1="20" x2="12" y2="4"></line>
                      <line x1="6" y1="20" x2="6" y2="14"></line>
                    </svg>
                  </div>
                  <span className="mobile-bottom-nav-label">Модерация</span>
                </button>
                <button 
                  className={`mobile-bottom-nav-item ${mobileBottomMenuOpen.management || ['managers', 'payouts', 'vacations', 'settings', 'users', 'applications'].includes(activeNav) ? 'active' : ''}`}
                  onClick={() => toggleSubmenu('management')}
                >
                  <div className="mobile-bottom-nav-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                      <path d="M2 17l10 5 10-5"></path>
                      <path d="M2 12l10 5 10-5"></path>
                    </svg>
                  </div>
                  <span className="mobile-bottom-nav-label">Управление</span>
                </button>
              </>
            <button 
              className={`mobile-bottom-nav-item ${mobileBottomMenuOpen.profile || isSettingsOpen || notificationsModalOpen ? 'active' : ''} ${hasUnreadNotifications ? 'has-unread-notification' : ''}`}
              onClick={() => toggleSubmenu('profile')}
            >
              <div className="mobile-bottom-nav-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                {hasUnreadNotifications && <span className="mobile-profile-unread-dot" aria-label="Есть новое уведомление" />}
              </div>
              <span className="mobile-bottom-nav-label">Профиль</span>
            </button>
          </nav>
        </>
      )}
      
      <AccessDeniedModal 
        isOpen={showAccessDenied} 
        message={accessDeniedMessage}
      />

      {/* Предложение установить PIN после входа без PIN (с нового IP) — см. useEffect с user.birthDate */}
      {showSetPinPrompt && (
        <div className="set-pin-prompt-overlay">
          <div className="set-pin-prompt-backdrop" />
          <div className="set-pin-prompt-card">
            <div className="set-pin-prompt-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <h3 className="set-pin-prompt-title">Установите PIN-код</h3>
            <p className="set-pin-prompt-desc">
              Для быстрого входа с этого устройства и входа с нового IP без повторного ввода пароля установите PIN в настройках профиля.
            </p>
            <div className="set-pin-prompt-actions">
              <button
                type="button"
                className="set-pin-prompt-btn primary"
                onClick={() => {
                  setShowSetPinPrompt(false);
                  setIsSettingsOpen(true);
                }}
              >
                Установить в профиле
              </button>
              <button
                type="button"
                className="set-pin-prompt-btn secondary"
                onClick={() => setShowSetPinPrompt(false)}
              >
                Позже в профиле
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// Компонент для контента "Смены"
const ShiftsContent = () => {
  return <Shifts />;
};

export default Dashboard;
